import { useEffect, useRef, useState } from "react";

export interface TabItem<T extends string> {
  id: T;
  label: string;
  count?: number;
}

/**
 * A row of text tabs with an underline on the current one. When there are more than fit (a phone),
 * the row scrolls sideways and fades at the edge that has more behind it.
 */
export function Tabs<T extends string>({ label, items, current, onSelect }: { label: string; items: Array<TabItem<T>>; current: T; onSelect: (id: T) => void }) {
  const nav = useRef<HTMLElement>(null);
  const [more, setMore] = useState({ before: false, after: false });

  useEffect(() => {
    const element = nav.current;
    if (!element) return;
    const measure = () => setMore({ before: element.scrollLeft > 1, after: element.scrollLeft + element.clientWidth < element.scrollWidth - 1 });
    measure();
    element.addEventListener("scroll", measure, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(element);
    return () => { element.removeEventListener("scroll", measure); observer?.disconnect(); };
  }, [items.length]);

  useEffect(() => {
    nav.current?.querySelector<HTMLElement>('[aria-current="page"]')?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [current]);

  return <nav ref={nav} className="bw-area-tabs" aria-label={label} data-more-before={more.before || undefined} data-more-after={more.after || undefined}>
    {items.map((item) => <button key={item.id} type="button" aria-current={current === item.id ? "page" : undefined} onClick={() => onSelect(item.id)}>{item.label}{item.count !== undefined && <span>{item.count}</span>}</button>)}
  </nav>;
}
